import { useState } from "react";

// Third Party
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";

// Custom Hooks
import useUserToken from "@/hook/useUserToken";

// Components
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

// Icons
import { ArrowLeft, PlusCircle } from "lucide-react";

// Services
import { getUserList } from "@/services/chat";
import type { UserList } from "@/types/chat";
import { CHAT_AVATAR_EXTRA_SMALL } from "@/components/constants";

export const Route = createFileRoute(
    "/_layout-private/chat/_layout/private/create",
)({
    component: RouteComponent,
});

function RouteComponent() {
    const user = useUserToken();
    const navigate = useNavigate();

    // State
    const [search, setSearch] = useState("");
    const [debounceSearch] = useDebouncedValue(search, 500);
    const [selectedUser, setSelectedUser] = useState(
        {} as {
            profileUrl: string | undefined;
            fullName: string | undefined;
            phone: string | undefined;
            receiverId: string | undefined;
        },
    );

    // Query
    const { data, isLoading } = useQuery({
        queryKey: ["user-lists", debounceSearch, user?.data?.userChatID],
        queryFn: () =>
            getUserList({
                userId: user?.data?.userChatId,
                search: debounceSearch,
                role: "ADMIN"
            }),
    });

    console.log(data?.data, "User List");

    const lists = data?.data as UserList[];

    return (
        <div className="flex flex-col w-full overflow-y-auto min-h-[calc(100vh-200px)] gap-4 ">
            <header className="flex relative justify-center items-center p-4 border-b">
                <Link to="/chat" className="flex absolute left-4 items-center text-sm">
                    <ArrowLeft className="mr-2 w-[16px] h-[16px]" />
                    <span>ກັບຄືນ</span>
                </Link>

                <h2 className="font-bold text-[18px]">ສ້າງສົນທະນາສ່ວນຕົວໃໝ່</h2>
            </header>

            <div className="w-[65%]  overflow-y-auto mx-auto">
                <Input
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ຄົ້ນຫາຊື່ລັດວິສາຫະກິດ"
                />
                <small className="text-muted-foreground">
                    ລາຍຊື່ວິສາຫະກິດທັງໝົດ ({lists?.length}/{data?.total})
                </small>

                <div className="flex flex-col w-full h-[45vh] overflow-y-auto gap-4 mt-4">
                    {isLoading
                        ? Array.from({ length: 10 }).map((_, i) => (
                            <Skeleton key={i} className="h-[70px] rounded-lg" />
                        ))
                        : lists?.map((soe: UserList) => (
                            <div
                                key={soe._id}
                                className={cn(
                                    "relative flex items-center gap-4 px-4 py-1 rounded-lg shadow cursor-pointer",
                                    selectedUser?.receiverId === soe?._id &&
                                    "border border-primary ",
                                )}
                                onClick={() =>
                                    setSelectedUser({
                                        profileUrl: soe?.profileUrl,
                                        fullName: soe?.fullName,
                                        phone: soe?.phone,
                                        receiverId: soe?._id,
                                    })
                                }
                                onKeyDown={() => { }}
                            >
                                <Avatar className="w-[48px] h-[48px] flex items-center justify-center">
                                    <AvatarImage
                                        src={CHAT_AVATAR_EXTRA_SMALL + soe?.profileUrl}
                                    />
                                    <AvatarFallback>{soe?.fullName?.charAt(0)}</AvatarFallback>
                                </Avatar>

                                <div className="text-sm">
                                    <h2>{soe?.fullName}</h2>
                                    <p className="text-muted-foreground">
                                        {soe?.phone || "*********"}
                                    </p>
                                </div>
                            </div>
                        ))}
                </div>

                {selectedUser ? (
                    <div className="flex justify-end items-center mt-4 w-full">
                        <Button
                            onClick={() =>
                                navigate({
                                    to: "/chat/$id",
                                    params: {
                                        id: "TEMP",
                                    },
                                    search: {
                                        receiverId: selectedUser?.receiverId,
                                        profileUrl: selectedUser?.profileUrl,
                                        fullName: selectedUser?.fullName,
                                        phone: selectedUser?.phone,
                                    },
                                })
                            }
                        >
                            ສ້າງການສົນທະນາ <PlusCircle />
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
